
/**
 * GSC CALIBRATION ENGINE v9.3 - ROBUST HARDWARE MAPPING
 */

export interface CalibrationResult {
    success: boolean;
    latencySec?: number;
    confidence: number;
    hardwareType?: 'WIRED' | 'WIRELESS' | 'UNKNOWN';
    error?: 'LOW_SIGNAL' | 'NO_CORRELATION' | 'CLOCK_DESYNC' | 'AEC_INTERFERENCE' | 'NEGATIVE_DRIFT' | 'HARDWARE_FAULT';
}

export const analyzeLatency = (
    rawBuffer: Float32Array,
    expectedPingTimes: number[],
    sampleRate: number
): CalibrationResult => {
    const detectedOffsets: number[] = [];
    const windowSize = Math.floor(sampleRate * 0.020); // 20ms window
    
    // 1. ANALYSE DU BRUIT AMBIANT
    let noiseEnergy = 0;
    const scanLen = Math.min(rawBuffer.length, sampleRate);
    for (let i = 0; i < scanLen; i++) noiseEnergy += rawBuffer[i] * rawBuffer[i];
    const avgNoise = Math.sqrt(noiseEnergy / scanLen) || 0.001;
    
    // Seuil adaptatif : au moins 5x le bruit de fond
    const snrThreshold = Math.max(0.1, avgNoise * 5.0); 

    // 2. SCAN TEMPOREL POUR CHAQUE PING
    expectedPingTimes.forEach((expectedTime) => {
        const searchStart = Math.max(0, Math.floor((expectedTime - 0.05) * sampleRate));
        const searchEnd = Math.min(rawBuffer.length - windowSize, Math.floor((expectedTime + 0.8) * sampleRate));

        let bestPeakIdx = -1;
        let maxSlope = 0;

        for (let i = searchStart; i < searchEnd; i++) {
            // On cherche le front montant le plus raide (transient attack)
            const slope = Math.abs(rawBuffer[i] - rawBuffer[i - 1] || 0);
            if (slope > maxSlope && Math.abs(rawBuffer[i]) > snrThreshold) {
                maxSlope = slope;
                bestPeakIdx = i;
            }
        }

        if (bestPeakIdx !== -1) {
            detectedOffsets.push((bestPeakIdx / sampleRate) - expectedTime);
        }
    });

    if (detectedOffsets.length < 2) {
        return { success: false, confidence: 0, error: 'LOW_SIGNAL' };
    }

    // 3. RECHERCHE DU CLUSTER LE PLUS STABLE
    detectedOffsets.sort((a, b) => a - b);
    let bestCluster: number[] = [];
    const CLUSTER_TOLERANCE = 0.015; // 15ms de gigue autorisée entre pings

    for (let i = 0; i < detectedOffsets.length; i++) {
        const current = detectedOffsets[i];
        const cluster = detectedOffsets.filter(o => Math.abs(o - current) <= CLUSTER_TOLERANCE);
        if (cluster.length > bestCluster.length) {
            bestCluster = cluster;
        }
    }

    // Si on a un cluster majoritaire, on prend sa moyenne
    const finalOffset = bestCluster.length >= 2 
        ? bestCluster.reduce((a, b) => a + b, 0) / bestCluster.length
        : detectedOffsets[0];

    // PROTECTION PHYSIQUE
    if (finalOffset > 1.2) return { success: false, confidence: 0, error: 'HARDWARE_FAULT' };
    if (finalOffset < -0.050) return { success: false, confidence: 0, error: 'NEGATIVE_DRIFT', latencySec: finalOffset };
    
    const safeOffset = Math.max(0, finalOffset);
    const hardwareType = safeOffset > 0.160 ? 'WIRELESS' : 'WIRED';

    return {
        success: true,
        latencySec: safeOffset,
        hardwareType,
        confidence: bestCluster.length / expectedPingTimes.length
    };
};
